/**
 * ProgressionTimeline — the operational heartbeat of the Deal Room.
 *
 * Renders the canonical UK property-transaction journey
 * (Offer · Memo · AML · Mortgage · Searches · Survey · Contract ·
 * [BSA] · [Mgmt Pack] · Enquiries · Report · Exchange · Complete) as
 * a calm, premium vertical timeline.
 *
 * iter-292 — Single source of truth.
 *   The local MILESTONES array + STAGE_TO_PROGRESS map were
 *   removed. The component now consumes the canonical journey
 *   module (`/app/frontend/src/data/transactionJourney.js`) so
 *   every timeline surface (rail, vertical timeline, sales
 *   progression rail, deal command rail) shares the same shape,
 *   ordering, gating and copy. Internal stage ids are preserved
 *   behind the scenes.
 *
 * Each milestone exposes:
 *   - status          (Completed | In progress | Awaiting | Blocked | At risk)
 *   - assigned party  (e.g. "Buyer's solicitor")
 *   - timestamp       (completed_at or due_at)
 *   - optional notes
 *
 * Visual identity matches the existing light-mode Deal Room card surface
 * (LIGHT tokens, soft shadow, rounded-2xl). Motion is restrained:
 * milestones settle in on mount with a 60ms stagger and active rows
 * carry a subtle pulse indicator. No new endpoints — milestone states
 * are synthesised from existing `deal`, `documents`, `docRequests`,
 * and `intel` data so this ships as a purely additive tab.
 */
import React, { useMemo, useState } from "react";
import {
  CheckCircle2, Clock, AlertTriangle, ShieldAlert, FileText,
  KeyRound, Handshake, Search, MailCheck, FileSignature, PackageCheck,
  CalendarCheck, ClipboardCheck, Sparkles,
} from "lucide-react";
import { LIGHT } from "./RoomFlow";
import { fullJourneyFor, fullJourneyIndexFor, optsForDeal } from "../../data/transactionJourney";

/* iter-292 — Resolve lucide icon by canonical `iconName` carried on
 * each journey node. Centralised here so the journey module stays
 * free of React-specific imports. */
const ICONS = {
  Handshake, MailCheck, ShieldAlert, FileText, Search, KeyRound,
  FileSignature, PackageCheck, CalendarCheck, ClipboardCheck,
};

/* ── State tokens — calm operational accents, never alarming ─────────── */
const STATE = {
  completed:   { fg: "#10B981", bg: "#ECFDF5", border: "#D1FAE5", label: "Completed",   Icon: CheckCircle2 },
  in_progress: { fg: "#7C3AED", bg: "#F5F3FF", border: "#E9D5FF", label: "In progress", Icon: Sparkles },
  awaiting:    { fg: "#94A3B8", bg: "#F8FAFC", border: "#E2E8F0", label: "Awaiting",    Icon: Clock },
  blocked:     { fg: "#EF4444", bg: "#FEF2F2", border: "#FECACA", label: "Blocked",     Icon: ShieldAlert },
  at_risk:     { fg: "#F59E0B", bg: "#FFFBEB", border: "#FDE68A", label: "At risk",     Icon: AlertTriangle },
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

const daysUntil = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    return Math.round((d - now) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

/* Synthesise milestone state from existing deal context.
   Pure derivation — no new API needed.

   iter-292 — Drives off the canonical journey module so the
   vertical timeline shares the same nodes, ordering and tenure
   gating as the Deal Rooms list rail. The full timeline
   (pre-journey + journey) is rendered so the operator sees the
   complete conveyancing story; the active index resolver uses
   `fullJourneyIndexFor` which respects internal stage ids. */
const buildMilestoneStates = (deal, documents, docRequests, intel, momentum) => {
  const tenure = String(deal?.tenure || deal?.property?.tenure || "").toLowerCase();
  /* iter-306 — Vertical timeline shares the canonical opts bag so
   * Mortgage / Mgmt Pack / BSA nodes appear in lockstep with the
   * Deal Rooms list rail and the canonical journey strip. */
  const opts = optsForDeal({ ...deal, tenure, applicableBSA: deal?.applicableBSA });
  const nodes = fullJourneyFor(tenure, opts);
  const stage = String(deal?.stage || "offer").toLowerCase();
  const stageProgress = fullJourneyIndexFor(tenure, stage, opts);

  const docsByType = new Map();
  (documents || []).forEach((d) => {
    const key = String(d.doc_type || d.type || "").toLowerCase();
    if (key) docsByType.set(key, d);
  });
  const reqsByType = new Map();
  (docRequests || []).forEach((r) => {
    const key = String(r.doc_type || r.type || "").toLowerCase();
    if (key) reqsByType.set(key, r);
  });

  // Risk signals that escalate milestones from "in_progress" -> "at_risk" / "blocked"
  const solicitorDelayed = (intel?.solicitor_response?.score ?? 100) < 45;
  const mortgageExpiringSoon = (intel?.mortgage_expiry?.days_left ?? 999) <= 14;
  const chainFragile = (intel?.chain_stability?.score ?? 100) < 50;

  return nodes.map((m, idx) => {
    let status;
    if (idx < stageProgress) status = "completed";
    else if (idx === stageProgress) status = "in_progress";
    else status = "awaiting";

    // Doc-based completion overrides for known types — keyed on
    // the canonical journey node id.
    if (m.id === "memo"     && docsByType.has("memorandum"))     status = "completed";
    if (m.id === "contract" && docsByType.has("draft_contract")) status = "completed";
    if (m.id === "mortgage" && docsByType.has("mortgage_offer")) status = "completed";
    if (m.id === "aml"      && docsByType.has("aml"))            status = "completed";

    // Risk escalation on the active milestone only — keeps UI calm.
    if (status === "in_progress") {
      if (m.id === "mortgage"  && mortgageExpiringSoon) status = "at_risk";
      if (m.id === "contract"  && solicitorDelayed)    status = "at_risk";
      if (m.id === "enquiries" && solicitorDelayed)    status = "blocked";
      if (m.id === "exchange"  && chainFragile)        status = "at_risk";
    }

    // Synthetic dates (stable-deterministic) — uses deal.id hash so same deal
    // shows consistent dates between renders. Real wiring: deal.timeline_at[m.id].
    const seed = (deal?.id || "x").charCodeAt(0) % 7;
    const completedDate = status === "completed"
      ? new Date(Date.now() - (nodes.length - idx + seed) * 86400000 * 1.7).toISOString()
      : null;
    const dueDate = status === "in_progress" || status === "at_risk" || status === "blocked"
      ? new Date(Date.now() + (idx - stageProgress + 2 + seed) * 86400000 * 1.4).toISOString()
      : null;

    // Notes specific to risk states — calm, operational tone
    let note = "";
    if (status === "at_risk" && m.id === "mortgage" && intel?.mortgage_expiry?.days_left != null) {
      note = `Mortgage offer expires in ${intel.mortgage_expiry.days_left}d — confirm exchange target.`;
    } else if (status === "at_risk" && m.id === "contract") {
      note = "Solicitor responsiveness slowing — chase recommended.";
    } else if (status === "blocked" && m.id === "enquiries") {
      note = "Outstanding enquiries with no recent solicitor response.";
    } else if (status === "at_risk" && m.id === "exchange") {
      note = "Chain fragility detected — confirm linked parties.";
    }

    return {
      id: m.id,
      title: m.longLabel,
      party: m.party,
      Icon: ICONS[m.iconName] || FileText,
      pre: !!m.pre,
      status,
      completedDate,
      dueDate,
      note,
    };
  });
};

/* ── A single timeline row ───────────────────────────────────────────── */
const TimelineRow = ({ milestone, isLast, index }) => {
  const s = STATE[milestone.status];
  const Icon = milestone.Icon;
  const StateIcon = s.Icon;
  const isActive = milestone.status === "in_progress" || milestone.status === "at_risk" || milestone.status === "blocked";

  const dueLabel = (() => {
    if (milestone.status === "completed" && milestone.completedDate) return `Completed · ${fmtDate(milestone.completedDate)}`;
    if (!milestone.dueDate) return "";
    const d = daysUntil(milestone.dueDate);
    if (d == null) return "";
    if (d < 0) return `Overdue · ${Math.abs(d)}d`;
    if (d === 0) return "Due today";
    if (d === 1) return "Due tomorrow";
    return `Due in ${d}d`;
  })();

  return (
    <li
      data-testid={`milestone-row-${milestone.id}`}
      data-status={milestone.status}
      className="relative cf-milestone-in"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-start gap-4 pb-5 sm:pb-6">
        {/* Spine */}
        <div className="relative shrink-0 flex flex-col items-center">
          <span
            className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0 relative"
            style={{
              background: s.bg,
              border: `1.5px solid ${s.border}`,
              color: s.fg,
            }}
          >
            <Icon size={15} strokeWidth={2.2} />
            {isActive && (
              <span
                className="absolute inset-0 rounded-full cf-milestone-pulse pointer-events-none"
                style={{ background: s.fg, opacity: 0.18 }}
              />
            )}
            {milestone.status === "completed" && (
              <span
                className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full inline-flex items-center justify-center"
                style={{ background: "#10B981", border: "2px solid #FFFFFF" }}
              >
                <CheckCircle2 size={9} className="text-white" strokeWidth={3} />
              </span>
            )}
          </span>
          {!isLast && (
            <span
              aria-hidden="true"
              className="w-px flex-1 mt-1 mb-[-22px]"
              style={{
                background: milestone.status === "completed"
                  ? `linear-gradient(180deg, ${STATE.completed.fg}55 0%, ${LIGHT.border} 100%)`
                  : LIGHT.border,
                minHeight: 36,
              }}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pt-1">
          <div className="flex items-start justify-between gap-3">
            <p
              className="text-[13px] sm:text-[13.5px] font-bold leading-tight"
              style={{
                color: milestone.status === "awaiting" ? LIGHT.textMuted : LIGHT.textPrimary,
                textDecoration: milestone.status === "completed" ? "none" : "none",
              }}
            >
              {milestone.title}
            </p>
            <span
              data-testid={`milestone-status-${milestone.id}`}
              className="inline-flex items-center gap-1 px-2 h-[22px] rounded-full text-[10px] font-bold uppercase tracking-[0.12em] shrink-0 whitespace-nowrap"
              style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
            >
              <StateIcon size={9.5} strokeWidth={2.6} />
              {s.label}
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]" style={{ color: LIGHT.textMuted }}>
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1 h-1 rounded-full" style={{ background: LIGHT.textSubtle }} />
              {milestone.party}
            </span>
            {dueLabel && (
              <span
                className="font-semibold"
                style={{
                  color: milestone.status === "completed"
                    ? LIGHT.textMuted
                    : (milestone.status === "blocked" || milestone.status === "at_risk")
                      ? s.fg
                      : LIGHT.textMuted,
                }}
              >
                {dueLabel}
              </span>
            )}
          </div>

          {milestone.note && (
            <p
              className="mt-2 text-[11px] leading-snug px-2.5 py-1.5 rounded-md inline-block"
              style={{ background: s.bg, color: s.fg, border: `1px solid ${s.border}` }}
            >
              {milestone.note}
            </p>
          )}
        </div>
      </div>
    </li>
  );
};

/* ═════════════════════════════════════════════════════════════════════
   Public component
   ──────────────────────────────────────────────────────────────────── */
export const ProgressionTimeline = ({ deal, documents = [], docRequests = [], intel = null, momentum = null }) => {
  const items = useMemo(
    () => buildMilestoneStates(deal, documents, docRequests, intel, momentum),
    [deal, documents, docRequests, intel, momentum]
  );

  /* Summary band — total / completed / next due. Calm, scannable. */
  const completed = items.filter((m) => m.status === "completed").length;
  const totalDone = completed;
  const total = items.length;
  const pct = Math.round((totalDone / total) * 100);
  const next = items.find((m) => m.status === "in_progress" || m.status === "at_risk" || m.status === "blocked");

  const [expanded, setExpanded] = useState(true); // future-proof — currently always open

  return (
    <section
      data-testid="dealroom-progression-timeline"
      className="rounded-2xl overflow-hidden"
      style={{ background: LIGHT.cardBg, border: `1px solid ${LIGHT.border}`, boxShadow: LIGHT.shadowSoft }}
    >
      {/* Header — operational summary */}
      <header
        className="px-5 sm:px-6 py-5 flex items-start justify-between gap-4"
        style={{ borderBottom: `1px solid ${LIGHT.border}` }}
      >
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: "#7C3AED" }}>
            Progression timeline
          </p>
          <h3 className="text-[16px] sm:text-[17px] font-bold mt-1.5 leading-tight" style={{ color: LIGHT.textPrimary }}>
            Operational heartbeat
          </h3>
          {next ? (
            <p className="text-[12px] mt-1.5 leading-relaxed" style={{ color: LIGHT.textMuted }}>
              <span className="font-semibold" style={{ color: LIGHT.textPrimary }}>Next · </span>
              {next.title} <span style={{ color: LIGHT.textSubtle }}>· {next.party}</span>
            </p>
          ) : (
            <p className="text-[12px] mt-1.5" style={{ color: LIGHT.textMuted }}>All milestones complete.</p>
          )}
        </div>

        {/* Compact progress ring */}
        <div className="relative w-[68px] h-[68px] shrink-0">
          <svg viewBox="0 0 36 36" className="w-[68px] h-[68px] -rotate-90">
            <circle cx="18" cy="18" r="15.9" fill="none" stroke="#EEF2F7" strokeWidth="3.2" />
            <circle
              cx="18" cy="18" r="15.9"
              fill="none" stroke="#7C3AED" strokeWidth="3.2"
              strokeDasharray={`${pct},100`} strokeLinecap="round"
              style={{ transition: "stroke-dasharray 600ms cubic-bezier(0.22, 1, 0.36, 1)" }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="font-bold text-[15px] tabular-nums leading-none" style={{ color: LIGHT.textPrimary }}>
              {totalDone}/{total}
            </span>
            <span className="text-[9px] font-semibold uppercase tracking-[0.12em] mt-0.5" style={{ color: LIGHT.textSubtle }}>
              Steps
            </span>
          </div>
        </div>
      </header>

      {/* Timeline */}
      <ol className="px-5 sm:px-6 pt-6 pb-2 list-none">
        {items.map((m, i) => (
          <TimelineRow key={m.id} milestone={m} isLast={i === items.length - 1} index={i} />
        ))}
      </ol>

      {/* Inline keyframes — restrained, infrastructure-grade motion */}
      <style>{`
        .cf-milestone-in {
          opacity: 0;
          transform: translateY(4px);
          animation: cf-mile-in 480ms cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes cf-mile-in { to { opacity: 1; transform: translateY(0); } }
        .cf-milestone-pulse {
          animation: cf-mile-pulse 2400ms ease-in-out infinite;
        }
        @keyframes cf-mile-pulse {
          0%, 100% { transform: scale(1);   opacity: 0.18; }
          50%      { transform: scale(1.25); opacity: 0.04; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cf-milestone-in, .cf-milestone-pulse { animation: none !important; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
};
